import classNames from 'classnames'
import IconRender from 'components/common/IconRender'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomColorsRef, Filters, IconSizes } from 'types/general'

import FilterDateRange from './FilterDateRange'
import FilterDropdown from './FilterDropdown'
import FilterLocationInputs from './FilterLocationInputs'
import FilterPriceRange from './FilterPriceRange'
import FilterTags from './FilterTags'
import { RESIDENTIAL_UNIT_TYPE_OPTIONS } from './residentialUnitTypes'
import SwitchList from './SwitchList'

interface FilterComponentProps {
  filterOptions: any
  filters: Filters
  setFilters: (filters: Filters) => void
  handleChange: (key: string, value: any) => void
  onFilter?: () => void
  onReset?: () => void
  hasAppliedFilters: boolean
}

const MobileFilterBar = ({
  filterOptions,
  filters,
  setFilters,
  handleChange,
  onFilter,
  onReset,
  hasAppliedFilters,
}: FilterComponentProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [, setOpenAccordions] = useState<string[]>([])

  const toggleFilterBar = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setOpenAccordions([])
    }
  }

  useEffect(() => {
    const openMobile = () => {
      setIsOpen(true)
      setOpenAccordions([])
    }
    window.addEventListener('openMobileFilters', openMobile)
    return () => window.removeEventListener('openMobileFilters', openMobile)
  }, [])
 
  return (
    <>
      {/* Floating filter button — mobile only; not a fixed sidebar */}
      {!isOpen ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))] lg:hidden">
          <button
            type="button"
            onClick={toggleFilterBar}
            aria-expanded={false}
            aria-label={t('filters.filters')}
            className="pointer-events-auto inline-flex min-h-[48px] items-center gap-2 rounded-full bg-imovPrimary px-5 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-imovPrimary/90"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
              />
            </svg>
            <span>{t('filters.filters')}</span>
            {hasAppliedFilters ? (
              <span
                className="inline-flex h-2 w-2 rounded-full bg-white"
                aria-hidden
              />
            ) : null}
          </button>
        </div>
      ) : null}

      {/* Mobile Filter Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end">
          <div className="bg-white w-full max-h-[85vh] rounded-t-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-300">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('filters.filters')}
              </h2>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleFilterBar()
                }}
                className="mx-2 p-1 flex justify-center items-center text-white rounded-lg transition-colors duration-200 touch-manipulation"
                style={{
                  minWidth: '48px',
                  minHeight: '48px',
                  zIndex: 1000
                }}
                type="button"
              >
                <IconRender
                  icon="regular-xmark"
                  size={IconSizes.LG}
                  color={CustomColorsRef.Primary}
                />
              </button>
            </div>

            {/* Accordion Content */}
            <div className="overflow-y-auto max-h-[calc(85vh-80px)]">
            <FilterDropdown
            icon={filterOptions.bedrooms.icon}
            label={filterOptions.bedrooms.label}
            options={filterOptions.bedrooms.options}
            value={filters.bedrooms}
            onChange={val => handleChange('bedrooms', val)}
            onFilter={onFilter}
            multiSelect
          />

          <FilterDropdown
            icon={filterOptions.bathrooms.icon}
            label={filterOptions.bathrooms.label}
            options={filterOptions.bathrooms.options}
            value={filters.bathrooms}
            onChange={val => handleChange('bathrooms', val)}
            onFilter={onFilter}
            multiSelect
          />

          <FilterDropdown
            icon={filterOptions.unitType.icon}
            label="filters.housingType"
            options={RESIDENTIAL_UNIT_TYPE_OPTIONS.map(opt => ({
              value: opt.value,
              label: opt.label,
            }))}
            value={filters.unitType}
            onChange={val => handleChange('unitType', val)}
            onFilter={onFilter}
            multiSelect
          />

          <FilterTags
            handleChange={handleChange}
            selectedTags={filters.tags}
            tags={filterOptions.tags.options}
            onFilter={onFilter}
          />

          <SwitchList
            icon={filterOptions.booleans.icon}
            label={filterOptions.booleans.label}
            options={filterOptions.booleans.options}
            value={filters.booleans}
            onChange={val => handleChange('booleans', val)}
            onFilter={onFilter}
          />

          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="mb-2 text-sm font-bold text-gray-900">{t('filters.location')}</p>
            <FilterLocationInputs
              city={filters.location?.city ?? ''}
              zipCode={filters.location?.zip_code ?? ''}
              neighborhoods={filters.location?.neighborhoods ?? []}
              onChange={value =>
                handleChange('location', {
                  ...(filters.location || {
                    zip_code: '',
                    full_address: '',
                    range: 10,
                  }),
                  city: value.city,
                  zip_code: value.zip_code,
                  neighborhoods: value.neighborhoods,
                  neighborhood: '',
                })
              }
            />
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="mb-2 text-sm font-bold text-gray-900">{t('filters.monthlyPrice')}</p>
            <FilterPriceRange
              compact
              priceMin={filters.priceMin}
              priceMax={filters.priceMax}
              onChange={(min, max) =>
                setFilters({
                  ...filters,
                  priceMin: min,
                  priceMax: max,
                  price: [],
                })
              }
            />
          </div>

          <FilterDateRange
            handleChange={handleChange}
            dateRangeOptions={filterOptions.date.options}
            label={filterOptions.date.label}
            icon={filterOptions.date.icon}
            filterKey={filterOptions.date.key}
          />
            </div>

            {/* Footer — filters already applied live; button only closes the sheet */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className={classNames('flex', hasAppliedFilters && onReset ? 'space-x-3' : '')}>
                {hasAppliedFilters && onReset ? (
                  <button
                    type="button"
                    onClick={() => {
                      onReset()
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {t('filters.clearAll')}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={toggleFilterBar}
                  className={classNames(
                    'min-h-[44px] px-4 py-2 bg-imovPrimary text-white rounded-lg hover:bg-imovPrimary/90 transition-colors',
                    hasAppliedFilters && onReset ? 'flex-1' : 'w-full'
                  )}
                >
                  {t('filters.seeResults')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MobileFilterBar
